import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Subscription, timeout } from 'rxjs';
import { AppConsts } from 'shared/AppConsts';
import { environment } from 'environments/environment';

export type ConnectivityBannerKind = 'offline' | 'server' | 'restored';

export interface ConnectivitySnapshot {
    banner: ConnectivityBannerKind | null;
    checking: boolean;
}

const HEALTHY_INTERVAL_MS = 30_000;
const DEGRADED_INTERVAL_MS = 5_000;
const PROBE_TIMEOUT_MS = 6_000;
const RESTORED_VISIBLE_MS = 4_000;
const PROBE_FAILURES_BEFORE_BANNER = 2;

@Injectable({
    providedIn: 'root',
})
export class ConnectivityService {
    readonly state$: BehaviorSubject<ConnectivitySnapshot>;

    private readonly state = new BehaviorSubject<ConnectivitySnapshot>({
        banner: null,
        checking: false,
    });

    private started = false;
    private degraded = false;
    private probeInFlight = false;
    private probeFailures = 0;
    private transportFailures = 0;
    private probeSub: Subscription | null = null;
    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private restoredTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private injector: Injector) {
        this.state$ = this.state;
    }

    start(): void {
        if (this.started || typeof window === 'undefined') {
            return;
        }

        this.started = true;
        window.addEventListener('online', this.onBrowserOnline);
        window.addEventListener('offline', this.onBrowserOffline);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.probe();
    }

    retry(): void {
        this.patch({ checking: true });
        this.probeSub?.unsubscribe();
        this.probeSub = null;
        this.probeInFlight = false;
        this.probe();
    }

    /**
     * A completed API response proves the server is reachable.
     * Ignored while healthy so ordinary traffic does not reset the heartbeat.
     */
    reportTransportSuccess(): void {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return;
        }

        if (this.state.value.banner === 'restored') {
            return;
        }

        if (
            this.state.value.banner === null &&
            this.probeFailures === 0 &&
            this.transportFailures === 0
        ) {
            return;
        }

        this.markReachable();
    }

    /**
     * Status 0 from a real API call. Confirmed with a health probe so a
     * cancelled request does not flash the banner.
     */
    reportTransportFailure(): void {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            this.showOffline();
            return;
        }

        if (this.state.value.banner === 'server' || this.state.value.banner === 'offline') {
            return;
        }

        this.transportFailures++;
        this.probe();
    }

    isMonitoredRequest(url: string): boolean {
        const base = this.apiBase();
        return !!base && url.startsWith(base);
    }

    isHealthRequest(url: string): boolean {
        return url.includes('/health');
    }

    private onBrowserOnline = (): void => {
        this.probeSub?.unsubscribe();
        this.probeSub = null;
        this.probeInFlight = false;
        this.probe();
    };

    private onBrowserOffline = (): void => {
        this.probeSub?.unsubscribe();
        this.probeSub = null;
        this.probeInFlight = false;
        this.showOffline();
    };

    private onVisibility = (): void => {
        if (document.hidden) {
            this.clearPoll();
            return;
        }

        this.probe();
    };

    private probe(): void {
        if (this.probeInFlight) {
            return;
        }

        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            this.showOffline();
            return;
        }

        const url = this.healthUrl();
        if (!url) {
            this.markReachable();
            return;
        }

        this.probeInFlight = true;
        const http = this.injector.get(HttpClient);
        this.probeSub = http
            .get(url, { observe: 'response', responseType: 'text' })
            .pipe(timeout(PROBE_TIMEOUT_MS))
            .subscribe({
                next: () => {
                    this.probeInFlight = false;
                    this.markReachable();
                },
                error: (error: unknown) => {
                    this.probeInFlight = false;

                    if (error instanceof HttpErrorResponse && error.status > 0) {
                        this.markReachable();
                        return;
                    }

                    this.handleProbeFailure();
                },
            });
    }

    private handleProbeFailure(): void {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            this.showOffline();
            return;
        }

        this.probeFailures++;
        const confirmed =
            this.probeFailures >= PROBE_FAILURES_BEFORE_BANNER ||
            this.transportFailures > 0;

        if (confirmed) {
            this.showServer();
            return;
        }

        this.patch({ checking: false });
        this.schedule(DEGRADED_INTERVAL_MS);
    }

    private showOffline(): void {
        this.clearRestoredTimer();
        this.degraded = true;
        this.probeFailures = 0;
        this.patch({ banner: 'offline', checking: false });
        this.schedule(DEGRADED_INTERVAL_MS);
    }

    private showServer(): void {
        this.clearRestoredTimer();
        this.degraded = true;
        this.patch({ banner: 'server', checking: false });
        this.schedule(DEGRADED_INTERVAL_MS);
    }

    private markReachable(): void {
        this.probeFailures = 0;
        this.transportFailures = 0;

        const current = this.state.value.banner;
        if (current === 'offline' || current === 'server' || this.degraded) {
            this.degraded = false;
            this.patch({ banner: 'restored', checking: false });
            this.clearRestoredTimer();
            this.restoredTimer = setTimeout(() => {
                if (this.state.value.banner === 'restored') {
                    this.patch({ banner: null, checking: false });
                }
            }, RESTORED_VISIBLE_MS);
        } else if (current !== 'restored') {
            this.patch({ banner: null, checking: false });
        }

        this.schedule(HEALTHY_INTERVAL_MS);
    }

    private patch(partial: Partial<ConnectivitySnapshot>): void {
        const current = this.state.value;
        const next: ConnectivitySnapshot = { ...current, ...partial };
        if (next.banner === current.banner && next.checking === current.checking) {
            return;
        }

        this.state.next(next);
    }

    private schedule(delayMs: number): void {
        this.clearPoll();
        if (typeof document !== 'undefined' && document.hidden) {
            return;
        }

        this.pollTimer = setTimeout(() => this.probe(), delayMs);
    }

    private clearPoll(): void {
        if (this.pollTimer !== null) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private clearRestoredTimer(): void {
        if (this.restoredTimer !== null) {
            clearTimeout(this.restoredTimer);
            this.restoredTimer = null;
        }
    }

    private healthUrl(): string | null {
        const base = this.apiBase();
        if (!base) {
            return null;
        }

        return `${base}/health?_=${Date.now()}`;
    }

    private apiBase(): string {
        const configured = AppConsts.remoteServiceBaseUrl || environment.remoteServiceBaseUrl || '';
        return configured.replace(/\/$/, '');
    }
}
